/**
 * HyperSpeedSlider
 *
 * Segmented control for ByteDance Hyper-SD step variants. Works for BOTH
 * base models: SD 1.5 (HYPER_BY_STEPS) and SDXL 1.0 (HYPER_SDXL_BY_STEPS).
 * The provider resolves the concrete LoRA per base_model at runtime, so the
 * same 1/2/4/8/12 step counts are valid for either checkpoint family.
 * 6 discrete values: Normal(0), Draft(1), Quick(2), Fast(4), Quality(8), NearBaseline(12).
 *
 * Dark theme only (RULE [[CSS Architecture]]).
 * Mobile-first: buttons wrap to 2 rows on narrow screens (RULE #13).
 * 44px touch targets (RULE #13).
 */

import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';

// ============================================================================
// Types
// ============================================================================

export type HyperSteps = 0 | 1 | 2 | 4 | 8 | 12;

export interface HyperSpeedSliderProps {
  value: HyperSteps;
  onChange: (v: HyperSteps) => void;
  /**
   * Base model of the selected checkpoint. Both SD 1.5 and SDXL 1.0 support
   * Hyper-SD steps (resolved per-family by the provider), so this is accepted
   * for API stability but no longer gates the control.
   */
  checkpointBaseModel: 'SD 1.5' | 'SD 1.5 Hyper' | 'SDXL 1.0';
  /**
   * Locks the control. Set when the selected checkpoint is already distilled
   * (Lightning/Hyper) — stacking a Hyper-SD LoRA there is double-distillation.
   */
  disabled?: boolean;
  /** Shown in place of the description when `disabled` is true. */
  disabledReason?: string;
}

interface StepOption {
  value: HyperSteps;
  labelKey: string;
  descriptionKey: string;
}

// ============================================================================
// Constants
// ============================================================================

const STEP_OPTIONS: StepOption[] = [
  {
    value: 0,
    labelKey: 'deforum.advanced.speedMode.normal',
    descriptionKey: 'deforum.advanced.speedMode.description.normal',
  },
  {
    value: 1,
    labelKey: 'deforum.advanced.speedMode.draft',
    descriptionKey: 'deforum.advanced.speedMode.description.draft',
  },
  {
    value: 2,
    labelKey: 'deforum.advanced.speedMode.quick',
    descriptionKey: 'deforum.advanced.speedMode.description.quick',
  },
  {
    value: 4,
    labelKey: 'deforum.advanced.speedMode.fast',
    descriptionKey: 'deforum.advanced.speedMode.description.fast',
  },
  {
    value: 8,
    labelKey: 'deforum.advanced.speedMode.quality',
    descriptionKey: 'deforum.advanced.speedMode.description.quality',
  },
  {
    value: 12,
    labelKey: 'deforum.advanced.speedMode.nearBaseline',
    descriptionKey: 'deforum.advanced.speedMode.description.nearBaseline',
  },
];

// ============================================================================
// Component
// ============================================================================

export function HyperSpeedSlider({
  value,
  onChange,
  disabled = false,
  disabledReason,
}: HyperSpeedSliderProps): React.ReactElement {
  const { t } = useTranslation();

  const activeOption = STEP_OPTIONS.find((o) => o.value === value) ?? STEP_OPTIONS[0];

  return (
    <div className={`space-y-2 ${disabled ? 'opacity-50' : ''}`}>
      <label className="form-label text-white/80 font-medium">
        {t('deforum.advanced.speedMode.label')}
      </label>

      <div
        className="flex flex-wrap gap-1.5"
        role="radiogroup"
        aria-label={t('deforum.advanced.speedMode.label')}
      >
        {STEP_OPTIONS.map((opt) => {
          const isActive = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              title={t(opt.descriptionKey)}
              className={`
                min-h-[44px] px-3 py-2 text-xs font-medium rounded-lg border transition-colors
                focus:outline-none focus:ring-2 focus:ring-purple-400/50
                flex flex-col items-center justify-center gap-0.5
                ${
                  isActive
                    ? 'bg-purple-700/40 border-purple-400 text-white'
                    : 'bg-black/30 border-white/10 text-white/70 hover:bg-black/40 hover:border-white/20'
                }
                disabled:cursor-not-allowed disabled:hover:bg-black/30 disabled:hover:border-white/10
              `}
            >
              <span>{t(opt.labelKey).split(' (')[0]}</span>
              {opt.value !== 0 && <span className="text-[10px] opacity-60">{opt.value}</span>}
              {opt.value === 0 && <span className="text-[10px] opacity-60">off</span>}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-white/50 mt-1">
        {disabled && disabledReason ? disabledReason : t(activeOption.descriptionKey)}
      </p>
    </div>
  );
}

export default HyperSpeedSlider;
